import React, { useState } from 'react';
import { ChevronDown, LogOut, User, KeyRound, X } from 'lucide-react';
import API from '../api';
import { showSuccess, showError } from '../utils/notification';
import { useAuth } from '../auth/AuthContext';

export default function AppHeader() {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const { user, role, signOut } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [curPwd, setCurPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  React.useEffect(() => { setFullName(user?.full_name || ''); }, [user?.full_name]);

  const saveName = async () => {
    if (!fullName.trim()) return showError('Full name is required');
    setSavingProfile(true);
    try {
      await API.put('/auth/profile', { full_name: fullName.trim() });
      showSuccess('Profile updated');
      // Force a soft reload so useAuth picks up new value from /auth/me
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      showError(err?.response?.data?.message || 'Failed to update profile');
    } finally { setSavingProfile(false); }
  };

  const changePwd = async () => {
    if (!curPwd || !newPwd) return showError('Both current and new passwords are required');
    setSavingProfile(true);
    try {
      const res = await API.post('/auth/change-password', {
        current_password: curPwd, new_password: newPwd,
      });
      showSuccess(res?.data?.message || 'Password changed. Please log in again.');
      setTimeout(() => { window.location.href = '/'; }, 800);
    } catch (err) {
      showError(err?.response?.data?.message || 'Failed to change password');
    } finally { setSavingProfile(false); }
  };

  const userInfo = {
    email: user?.email_id || user?.full_name || '',
    role,
  };

  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'ngo': return 'Organization User';
      case 'department':
        // WRD-specific role types override the generic L1/L2 label.
        if (user?.role_type) {
          if (user.role_type === 'L1')     return 'L1 Approver';
          if (user.role_type === 'L1 A')   return 'L1 A — Viewer';
          if (user.role_type === 'L2')     return 'L2 Approver';
          if (user.role_type === 'L2 A')   return 'L2 A — Viewer';
          if (user.role_type === 'Master') return 'Master — Viewer';
        }
        if (Number(user?.level) === 1) return 'L1 Approver';
        if (Number(user?.level) === 2) return 'L2 Approver';
        return 'Department User';
      case 'admin': return 'Admin User';
      case 'dept_admin': return 'Department Admin';
      case 'state': return 'State-Level User';
      default: return 'User';
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-brand-800 shadow-lg border-b-2 border-brand-accent font-['Inter',sans-serif]">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo and Title */}
          <div className="flex items-center space-x-3 sm:space-x-4">
            <img
              src="/images/tnlogo.png"
              alt="Tamil Nadu Government Logo"
              className="h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0"
            />
            <div className="hidden sm:block leading-tight">
              <h1 className="text-lg lg:text-xl font-semibold text-white">
                ஏரி இணையதளம்
              </h1>
              <p className="text-[0.9rem] text-brand-100 tracking-wide">
                தமிழ்நாடு அரசு
              </p>
            </div>
            <div className="sm:hidden">
              <h1 className="text-base font-bold text-white font-['Inter',sans-serif] tracking-tight">
                TN NOC Portal
              </h1>
              <p className="text-[0.9rem] text-brand-100">
                தமிழ்நாடு அரசு
              </p>
            </div>
          </div>

          {/* User Profile */}
          <div className="flex items-center">
            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center space-x-2 sm:space-x-3 bg-white/10 hover:bg-white/15 text-white px-2 sm:px-3 py-1.5 transition-colors duration-200 border border-white/15"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-brand-accent flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {userInfo.role ? userInfo.role.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-semibold text-white">{getRoleDisplayName(userInfo.role)}</div>
                  <div className="text-xs text-brand-100 truncate max-w-32 lg:max-w-none">{userInfo.email || 'User'}</div>
                </div>
                <div className="text-left sm:hidden">
                  <div className="text-xs font-semibold text-white">
                    {userInfo.role ? userInfo.role.toUpperCase() : 'USER'}
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-56 sm:w-64 bg-white shadow-xl border border-slate-200 border-t-4 border-t-brand-accent z-50 overflow-hidden">
                  <div className="py-1">
                    <div className="px-4 py-3 border-b border-slate-100 bg-brand-50">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-brand-700 flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {userInfo.role ? userInfo.role.charAt(0).toUpperCase() : 'U'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{getRoleDisplayName(userInfo.role)}</div>
                          <div className="text-xs text-gray-600 truncate">{userInfo.email || 'User'}</div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => { setShowProfile(true); setShowDropdown(false); }}
                      className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 transition-colors duration-200 group"
                    >
                      <User className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      <span className="font-medium">Profile & Password</span>
                    </button>
                    <button
                      onClick={signOut}
                      className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 transition-colors duration-200 group"
                    >
                      <LogOut className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      <span className="font-medium">Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        ></div>
      )}

      {showProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
            <div className="px-4 py-3 bg-brand-800 border-b-4 border-brand-accent flex justify-between items-center">
              <h3 className="text-base font-semibold text-white flex items-center gap-2"><User className="w-4 h-4" /> Profile</h3>
              <button onClick={() => setShowProfile(false)} className="text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-6 overflow-y-auto max-h-[70vh]">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email (cannot change)</label>
                <div className="px-3 py-2 text-sm bg-gray-50 border border-gray-200">{user?.email_id || '-'}</div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name</label>
                <input
                  className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-600"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
                <button
                  onClick={saveName}
                  disabled={savingProfile}
                  className="mt-2 px-3 py-1.5 text-sm font-medium text-white bg-brand-700 hover:bg-brand-800 disabled:opacity-50"
                >
                  {savingProfile ? 'Saving…' : 'Save Name'}
                </button>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4" /> Change Password</h4>
                <div className="space-y-2">
                  <input
                    type="password"
                    placeholder="Current password"
                    className="w-full px-3 py-2 text-sm border border-gray-300"
                    value={curPwd}
                    onChange={(e) => setCurPwd(e.target.value)}
                  />
                  <input
                    type="password"
                    placeholder="New password (min 8 chars, mixed case, digit, special)"
                    className="w-full px-3 py-2 text-sm border border-gray-300"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                  />
                  <ul className="text-xs space-y-0.5 mt-1">
                    {[
                      { ok: newPwd.length >= 8, text: 'At least 8 characters' },
                      { ok: /[a-z]/.test(newPwd), text: 'One lowercase letter' },
                      { ok: /[A-Z]/.test(newPwd), text: 'One uppercase letter' },
                      { ok: /[0-9]/.test(newPwd), text: 'One digit' },
                      { ok: /[^A-Za-z0-9]/.test(newPwd), text: 'One special character' },
                    ].map((r, i) => (
                      <li key={i} className={r.ok ? 'text-emerald-600' : 'text-gray-500'}>{r.ok ? '✓' : '○'} {r.text}</li>
                    ))}
                  </ul>
                  <button
                    onClick={changePwd}
                    disabled={savingProfile}
                    className="mt-2 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                  >
                    {savingProfile ? 'Updating…' : 'Change Password'}
                  </button>
                  <p className="text-xs text-gray-500 mt-1">You will be logged out after changing your password.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}